import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { brandAlpha, gradients, hoverOnly } from '../../theme/mixins';

/**
 * The stage's size is a constant, not a measurement.
 *
 * The crop maths needs to know how big the stage is, and the obvious way to find out --
 * `getBoundingClientRect`, or a `ResizeObserver` -- is the one thing that must not happen
 * here. Under jsdom a `ResizeObserver` is a no-op stub and every box measures 0x0, and in the
 * preview pane no observer callback or animation frame runs at all until a screenshot forces
 * a frame, so a stage that sized itself would render as nothing in both. Declaring the number
 * once and using it for both the `sx` and the arithmetic keeps CSS and JS agreeing without
 * either of them asking the layout engine.
 */
export const STAGE_SIZE = {
    compact: { width: 300, height: 300 },
    desktop: { width: 440, height: 320 },
} as const;

/** The published preview card is 220x76 with 8px of padding; match its inner box. */
export const PREVIEW_BOX = { width: 204, height: 60 } as const;

/** `APP_BAR_HEIGHT.md` and the header's own `maxHeight`, so preview 2 is honest. */
export const HEADER_BAR_HEIGHT = 72;

export const HEADER_LOGO_MAX_HEIGHT = 38;

export const dialogTitleStyles: SxProps<Theme> = { fontWeight: 700, pb: 1 };

export const hintStyles: SxProps<Theme> = { color: 'text.secondary', mb: 2 };

export const warningStyles: SxProps<Theme> = { mb: 2, borderRadius: 3 };

export const bodyStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: 3,
    alignItems: 'flex-start',
};

/**
 * The crop surface.
 *
 * `touchAction: 'none'` is not cosmetic: without it a touch drag scrolls the dialog instead
 * of panning the image, and the browser stops delivering `pointermove` partway through the
 * gesture, so the crop lurches and stops.
 */
export const stageStyles: SxProps<Theme> = {
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
    borderRadius: '12px',
    border: '1px solid rgba(16,24,40,0.12)',
    backgroundColor: '#f4f5f7',
    touchAction: 'none',
    cursor: 'move',
    userSelect: 'none',
};

export const stageImageStyles: SxProps<Theme> = {
    position: 'absolute',
    pointerEvents: 'none',
    // Nothing here should be smoothed away: the admin is judging edges.
    imageRendering: 'auto',
};

/** Everything outside the frame, dimmed by four bars rather than a clip-path. */
export const shadeStyles: SxProps<Theme> = {
    position: 'absolute',
    backgroundColor: 'rgba(16,24,40,0.45)',
    pointerEvents: 'none',
};

export const frameStyles: SxProps<Theme> = {
    position: 'absolute',
    border: '1px solid rgba(255,255,255,0.9)',
    boxShadow: '0 0 0 1px rgba(16,24,40,0.35)',
    pointerEvents: 'none',
};

export const handleStyles: SxProps<Theme> = {
    position: 'absolute',
    width: 18,
    height: 18,
    borderRadius: '4px',
    backgroundColor: '#fff',
    border: `2px solid ${brandAlpha('primary', 0.9)}`,
    pointerEvents: 'auto',
    touchAction: 'none',
    ...hoverOnly({ backgroundColor: brandAlpha('primary', 0.15) }),
};

export const controlsStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    mt: 2,
};

export const zoomSliderStyles: SxProps<Theme> = {
    flexGrow: 1,
    // MuiSlider is not in the theme's coarse-pointer tap-target list; give the rail a
    // little more vertical room so it is grabbable on a phone.
    py: 1.5,
};

export const previewsStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
};

export const previewLabelStyles: SxProps<Theme> = {
    color: 'text.secondary',
    display: 'block',
    mb: 0.5,
};

/**
 * Chequerboard, so an image with no transparency is obvious before it is discovered sitting
 * as a white slab on the frosted header. The same recipe as the published preview card in
 * `brandingSettings.styles.ts` -- duplicated rather than shared because the two boxes differ
 * in size and one of them lives in a dialog.
 */
export const chequerPreviewStyles: SxProps<Theme> = {
    width: PREVIEW_BOX.width,
    height: PREVIEW_BOX.height,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
    border: '1px solid rgba(16,24,40,0.12)',
    backgroundColor: '#fff',
    backgroundImage:
        'linear-gradient(45deg, #eef0f3 25%, transparent 25%), linear-gradient(-45deg, #eef0f3 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #eef0f3 75%), linear-gradient(-45deg, transparent 75%, #eef0f3 75%)',
    backgroundSize: '16px 16px',
    backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
};

/**
 * `backdropFilter` has nothing to blur unless something sits behind it, and a flat backdrop
 * would let an opaque logo hide in it. The brand wash is busy enough that a white rectangle
 * is unmistakable, which is the entire point of this preview.
 */
/**
 * The avatar previews, both circular, because a circle is the only shape a profile picture is
 * ever drawn in.
 *
 * Square boxes with a 50% radius rather than anything cleverer: the crop is already locked to
 * 1:1, so the mask takes nothing off and the preview shows exactly what will be stored. The
 * large one is the 88px circle from the profile page's account card, drawn at 2x so the
 * detail is visible while framing; the small one is the 36px avatar in the app bar, at its
 * real size, which is the question "will anyone be able to tell who this is at a glance".
 */
export const AVATAR_PREVIEW_BOX = { width: 176, height: 176 } as const;

export const HEADER_AVATAR_SIZE = 36;

export const avatarPreviewStyles: SxProps<Theme> = {
    width: AVATAR_PREVIEW_BOX.width,
    height: AVATAR_PREVIEW_BOX.height,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '50%',
    border: '1px solid rgba(16,24,40,0.12)',
    backgroundColor: '#fff',
};

/** The app-bar avatar at its real diameter, sitting in the real bar. */
export const headerAvatarPreviewStyles: SxProps<Theme> = {
    width: HEADER_AVATAR_SIZE,
    height: HEADER_AVATAR_SIZE,
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '50%',
    flexShrink: 0,
    backgroundColor: '#fff',
};

/** The name that sits to the left of that avatar in the real header, as a grey bar. */
export const headerAvatarNameStyles: SxProps<Theme> = {
    flex: 1,
    height: 10,
    mr: 1.5,
    borderRadius: 5,
    backgroundColor: 'rgba(16,24,40,0.10)',
};

export const headerPreviewBackdropStyles: SxProps<Theme> = {
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid rgba(16,24,40,0.12)',
    background: gradients.heroWash,
    pt: 2,
};

/** The real AppBar's own recipe, copied from the theme so this cannot drift politely. */
export const headerPreviewBarStyles: SxProps<Theme> = {
    height: HEADER_BAR_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    px: 2,
    backgroundColor: 'rgba(255,255,255,0.8)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(16,24,40,0.07)',
    boxShadow: '0 1px 12px rgba(16,24,40,0.05)',
};

/**
 * The browser tab the favicon preview draws the icon in, and the size the icon is drawn at.
 *
 * 16 is the real thing and not a scaled-down approximation: a tab icon is 16 CSS px in every
 * desktop browser, and the whole question this preview answers is whether the mark survives
 * being that small. Drawing it at 48 with a caption saying "shown at 16px" would answer a
 * question nobody has.
 *
 * The tab itself is roughly a Chrome tab's proportions -- wide enough for the icon and a few
 * words of title, which is the context that makes 16px legible or not.
 */
export const TAB_ICON_SIZE = 16;

export const TAB_PREVIEW_BOX = { width: 204, height: 96 } as const;

/** The window chrome behind the tab, so the tab reads as a tab rather than a grey pill. */
export const tabPreviewBackdropStyles: SxProps<Theme> = {
    width: TAB_PREVIEW_BOX.width,
    height: TAB_PREVIEW_BOX.height,
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid rgba(16,24,40,0.12)',
    background: gradients.heroWash,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    pt: 1.5,
    px: 1.5,
};

export const tabPreviewStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 1.25,
    py: 1,
    borderRadius: '8px 8px 0 0',
    // White, always: a browser tab is the browser's chrome and not this app's surface, and
    // the point of the preview is the icon against a colour the app does not choose.
    backgroundColor: '#fff',
    boxShadow: '0 -1px 6px rgba(16,24,40,0.08)',
};

/** The icon well, at the size a tab actually draws it. */
export const tabPreviewIconStyles: SxProps<Theme> = {
    width: TAB_ICON_SIZE,
    height: TAB_ICON_SIZE,
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
};

/** The page title beside it, as a grey bar -- the same device the app-bar name preview uses. */
export const tabPreviewTitleStyles: SxProps<Theme> = {
    flex: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: 'rgba(16,24,40,0.14)',
};

/**
 * The sheet the watermark preview draws the mark on: A4's own 210:297, so a mark that only
 * fits because the box is the wrong shape is not one this preview would pass.
 */
export const PAGE_PREVIEW_BOX = { width: 150, height: 212 } as const;

export const pagePreviewStyles: SxProps<Theme> = {
    width: PAGE_PREVIEW_BOX.width,
    height: PAGE_PREVIEW_BOX.height,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    border: '1px solid rgba(16,24,40,0.12)',
    boxShadow: '0 2px 10px rgba(16,24,40,0.10)',
    // White, always, and never a theme surface: this is paper. A watermark judged against a
    // tinted box would be judged against a page the printer will never produce.
    backgroundColor: '#fff',
};

/**
 * Lines of nothing, standing in for the document's text.
 *
 * The whole question this preview answers is "can the text still be read over the mark", so
 * there has to be something on the page to read. Drawn as a gradient rather than as dummy
 * words because it must not look like copy anybody should try to read -- and because the
 * real document is Romanian prose that would need translating to sit here.
 */
export const pagePreviewLinesStyles: SxProps<Theme> = {
    position: 'absolute',
    inset: '14px 12px',
    backgroundImage: 'repeating-linear-gradient(to bottom, rgba(16,24,40,0.42) 0 3px, transparent 3px 11px)',
    // The last line of a paragraph is short, and a page of identical full-width bars reads
    // as a barcode rather than as text.
    WebkitMaskImage: 'linear-gradient(to bottom, #000 0 88%, transparent 88%)',
    maskImage: 'linear-gradient(to bottom, #000 0 88%, transparent 88%)',
};

/** The mark itself, faint and centred, exactly as the server composes it. */
export const pagePreviewMarkStyles: SxProps<Theme> = {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

/** The window a preview shows the crop through. Its size is set per render. */
// --- Hero preview ---------------------------------------------------------
//
// The third surface this dialog previews on. A logo is judged against the frosted header, a
// watermark against the page it prints behind, and a hero against the band of text it sits
// under -- which is the only one of the three where the question is "can my headline still be
// read", and the reason this preview draws words rather than just the picture.

export const HERO_PREVIEW_BOX = { width: 240, height: 135 } as const;

/** The crop with nothing over it, for judging the photograph itself. */
export const plainPreviewStyles: SxProps<Theme> = {
    width: HERO_PREVIEW_BOX.width,
    height: HERO_PREVIEW_BOX.height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
};

export const heroPreviewStyles: SxProps<Theme> = {
    position: 'relative',
    width: HERO_PREVIEW_BOX.width,
    height: HERO_PREVIEW_BOX.height,
    overflow: 'hidden',
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
};

/**
 * The same wash the real hero lays over the photograph, at the same opacity.
 *
 * Duplicated from `landingPage.styles` as a number rather than imported, because the two are
 * different shapes -- one is a `background-image` gradient on the section itself, this is an
 * overlay box in a preview. If the page's scrim changes, change this in the same edit; a
 * preview that flatters the image more than the page does is worse than none.
 */
export const heroPreviewScrimStyles: SxProps<Theme> = {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.86)',
};

export const heroPreviewTextStyles: SxProps<Theme> = {
    position: 'relative',
    px: 2,
    textAlign: 'center',
    fontWeight: 700,
    lineHeight: 1.2,
    color: 'text.primary',
};

export const previewWindowStyles: SxProps<Theme> = {
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
};

export const previewImageStyles: SxProps<Theme> = {
    position: 'absolute',
    pointerEvents: 'none',
};

export const loadingStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
};

export const dialogActionsStyles: SxProps<Theme> = { px: 3, pb: 3, pt: 1 };
